<script setup lang="ts">
import { Switch } from "@headlessui/vue";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import Button from "~/components/dumb/Button.vue";
import ConfirmationDialog from "~/components/dumb/ConfirmationDialog.vue";
import SettingsRow from "~/components/settings/SettingsRow.vue";
import {
  APP_SUMO_TIER_TO_MAX_USERS,
  BILLING_FEATURES,
  HIDDEN_SUBSCRIPTIONS,
  ORDERED_SUBSCRIPTIONS,
  SUBSCRIPTION_TO_INDEX,
} from "~/constants/billing";
import { getQueryParam } from "~/router/common";
import { BillingPeriod, ButtonSize, ButtonStyle, DialogMode, Subscription } from "~/shared/enums";
import { usePageStore, useTenantStore } from "~/stores";
import { APPSUMO_ACCOUNT_URL, createOrUpdateSubscription, manageBilling } from "~/utils/billing";

type SubscriptionConfig = {
  subscription: Subscription;
  active: boolean;
  cost: number | string;
  costIsNumber: boolean;
  canProceedWithUpgrade: boolean;
  showButton: boolean;
  buttonDisabled: boolean;
  buttonText: string;
  buttonPrimary: boolean;
};

const router = useRouter();
const pageStore = usePageStore();
const tenantStore = useTenantStore();

const isBillingAnnually = ref(getQueryParam("monthly") !== "1");
if (!isBillingAnnually.value) {
  const query = { ...router.currentRoute.value.query };
  delete query.monthly;
  router.replace({ query });
}

const actionButtonWorking = ref<Subscription | null>(null);

const hasAppSumo = computed(() => tenantStore.appSumoTierNorm !== null);
const isDowngrading = computed(() => tenantStore.subscriptionDowngradeInitialized);

const shownSubscriptions = computed(() =>
  ORDERED_SUBSCRIPTIONS.filter(
    (e) =>
      tenantStore.subscriptionNorm === e ||
      !HIDDEN_SUBSCRIPTIONS.has(e) ||
      (e === Subscription.APP_SUMO && hasAppSumo.value)
  )
);
const shownSubscriptionConfigs = computed<SubscriptionConfig[]>(() => {
  const currentSubscriptionIndex = SUBSCRIPTION_TO_INDEX[tenantStore.subscriptionNorm];
  return shownSubscriptions.value.map((subscription) => {
    const subscriptionIndex = SUBSCRIPTION_TO_INDEX[subscription];

    const cost =
      subscription === Subscription.PERSONAL
        ? 0
        : subscription === Subscription.APP_SUMO
          ? `Tier ${tenantStore.appSumoTierNorm} • Lifetime`
          : subscription === Subscription.PREMIUM
            ? isBillingAnnually.value
              ? 8
              : 10
            : subscription === Subscription.BUSINESS
              ? isBillingAnnually.value
                ? 12
                : 15
              : "Custom";

    const active = tenantStore.subscriptionNorm === subscription;
    const isDowngradeTarget =
      isDowngrading.value &&
      (hasAppSumo.value ? subscription === Subscription.APP_SUMO : subscription === Subscription.PERSONAL);
    const isReUpgrade = isDowngrading.value && active;

    // This includes: regular upgrades, AppSumo tier upgrades, and re-upgrades
    const canProceedWithUpgrade =
      isReUpgrade ||
      (subscription === Subscription.APP_SUMO
        ? active && tenantStore.appSumoTierNorm !== null && tenantStore.appSumoTierNorm < 5
        : !tenantStore.isSubscriptionGte(subscription, true));

    const showButton = !(
      subscription === Subscription.PERSONAL &&
      hasAppSumo.value &&
      (!tenantStore.isSubscriptionGte(Subscription.PREMIUM, true) || isDowngrading.value)
    );

    const buttonDisabled =
      (active && !isDowngrading.value && !canProceedWithUpgrade) ||
      isDowngradeTarget ||
      (!!actionButtonWorking.value && actionButtonWorking.value !== subscription);

    const buttonText = isDowngradeTarget
      ? "Downgrading"
      : isReUpgrade
        ? "Re-upgrade"
        : active && canProceedWithUpgrade
          ? "Upgrade tier" // AppSumo tier upgrade (only case where active && canProceedWithUpgrade)
          : active
            ? "Current plan"
            : canProceedWithUpgrade
              ? subscription === Subscription.ENTERPRISE
                ? "Contact sales"
                : "Upgrade"
              : "Downgrade";

    const buttonPrimary =
      canProceedWithUpgrade &&
      ((hasAppSumo.value &&
        subscription === Subscription.APP_SUMO &&
        tenantStore.appSumoTierNorm !== null &&
        tenantStore.appSumoTierNorm < 5) ||
        subscriptionIndex === currentSubscriptionIndex + 1);

    return {
      subscription,
      active,
      cost,
      costIsNumber: typeof cost === "number",
      canProceedWithUpgrade,
      showButton,
      buttonDisabled,
      buttonText,
      buttonPrimary,
    };
  });
});
const billingTableRows = computed(() => {
  const shownSubscriptionSet = new Set(shownSubscriptions.value);
  return BILLING_FEATURES.map((feature) => {
    const row = {
      title: feature.title,
      values: [] as string[],
    };
    let value = "";
    ORDERED_SUBSCRIPTIONS.forEach((subscription) => {
      if (subscription in feature) {
        value = feature[subscription] as string;
      }
      // Override teammates value for AppSumo based on tier
      let resetValue;
      if (
        feature.title === "Teammates" &&
        subscription === Subscription.APP_SUMO &&
        tenantStore.appSumoTierNorm !== null
      ) {
        const maxUsers = APP_SUMO_TIER_TO_MAX_USERS[tenantStore.appSumoTierNorm];
        if (maxUsers !== undefined) {
          resetValue = value;
          value = `Up to ${maxUsers}`;
        }
      }
      if (shownSubscriptionSet.has(subscription)) {
        row.values.push(value);
      }
      if (resetValue) {
        value = resetValue;
      }
    });

    return row;
  });
});

const clickActionButton = async (subscriptionConfig: SubscriptionConfig, overrideDialogCheck = false) => {
  const { subscription, canProceedWithUpgrade } = subscriptionConfig;
  if (!(overrideDialogCheck || canProceedWithUpgrade)) {
    return;
  }

  if (
    (isDowngrading.value && !tenantStore.isSubscriptionGte(subscription, true)) ||
    (subscription === Subscription.APP_SUMO && !canProceedWithUpgrade)
  ) {
    manageBilling();
    return;
  }

  if (subscription === Subscription.APP_SUMO) {
    window.open(APPSUMO_ACCOUNT_URL, "_blank");
    return;
  }
  actionButtonWorking.value = subscription;

  const changed = await createOrUpdateSubscription(
    subscription,
    isBillingAnnually.value ? BillingPeriod.ANNUALLY : BillingPeriod.MONTHLY
  );
  if (changed) {
    tenantStore.subscription = subscription;
  }
  actionButtonWorking.value = null;
};
</script>

<template>
  <div v-if="!pageStore.isMobileApp">
    <div v-if="isDowngrading" class="mb-6 rounded-lg border p-4 bg-lt border-md">
      <div class="flex items-start gap-3">
        <div class="flex-1">
          <h3 class="mb-1 font-semibold text-warning-base">Subscription downgrade pending</h3>
          <p class="text-sm text-lt">
            Your subscription will be downgraded to the Personal plan at the end of your current billing period. You can
            re-upgrade at any time to keep your current features permanently.
          </p>
        </div>
        <Button :btn-style="ButtonStyle.PRIMARY" text="Re-upgrade" :size="ButtonSize.LARGE" @click="manageBilling" />
      </div>
    </div>
    <SettingsRow v-if="tenantStore.everSubscribed" title="Billing history" class="mb-8 mt-4">
      <Button
        :btn-style="ButtonStyle.SECONDARY"
        text="View invoices"
        :size="ButtonSize.LARGE"
        text-style="text-sm text-md"
        borderless
        class="rounded-md border !px-3 !py-1.5 shadow-sm border-md"
        @click="manageBilling" />
    </SettingsRow>
    <table :class="shownSubscriptionConfigs.length > 3 ? '-ml-20' : ''">
      <tbody>
        <tr>
          <th class="w-[34%] select-none text-left align-bottom font-normal text-lt">
            <div class="flex items-center py-2 pr-3">
              <div>Monthly</div>
              <Switch
                v-model="isBillingAnnually"
                :class="isBillingAnnually ? 'bg-primary-base' : 'bg-md'"
                class="relative mx-2 inline-flex h-5 w-8 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-ring-std"
                aria-label="Monthly/Annually toggle">
                <span
                  aria-hidden="true"
                  :class="isBillingAnnually ? 'translate-x-3' : 'translate-x-0'"
                  class="pointer-events-none inline-block rounded-full shadow ring-0 transition duration-200 ease-in-out bg-std icon-sm" />
              </Switch>
              <div>Annually</div>
            </div>
          </th>
          <th
            v-for="subscriptionConfig in shownSubscriptionConfigs"
            :key="subscriptionConfig.subscription"
            :class="subscriptionConfig.active && 'bg-lt'"
            class="w-[22%] min-w-40 border text-left align-top font-normal border-md">
            <div class="px-3 pb-3 pt-2">
              <div class="flex items-center justify-between">
                <div class="select-none text-xl text-md">{{ subscriptionConfig.subscription }}</div>
                <div
                  v-if="subscriptionConfig.costIsNumber && subscriptionConfig.cost !== 0 && isBillingAnnually"
                  class="ml-1 select-none rounded border px-1 py-0.5 text-xs text-lt border-hvy">
                  -20%
                </div>
              </div>
              <div v-if="subscriptionConfig.costIsNumber" class="mb-2 flex h-11 items-center gap-2">
                <div class="w-9 select-none text-xl text-md">${{ subscriptionConfig.cost }}</div>
                <div class="flex flex-col">
                  <div class="flex-1 select-none text-xs text-lt">per teammate</div>
                  <div class="flex-1 select-none text-xs text-lt">per month</div>
                </div>
              </div>
              <div v-else class="flex-1 select-none py-[18px] text-center text-xs text-lt">
                {{ subscriptionConfig.cost }}
              </div>
              <ConfirmationDialog
                v-if="subscriptionConfig.showButton"
                :disabled="subscriptionConfig.canProceedWithUpgrade"
                :mode="DialogMode.DELETE"
                title="Confirm downgrade"
                :description="`Are you sure you want to downgrade your plan? You'll lose access to many of the advanced features you rely on.`"
                confirm-text="Continue"
                cancel-text="Cancel"
                @confirm="clickActionButton(subscriptionConfig, true)">
                <Button
                  :btn-style="subscriptionConfig.buttonPrimary ? ButtonStyle.PRIMARY : ButtonStyle.SECONDARY"
                  :text="subscriptionConfig.buttonText"
                  :disabled="subscriptionConfig.buttonDisabled"
                  :working="actionButtonWorking === subscriptionConfig.subscription"
                  block
                  :size="ButtonSize.LARGE"
                  @click="clickActionButton(subscriptionConfig)" />
              </ConfirmationDialog>
            </div>
          </th>
        </tr>
        <tr v-for="row in billingTableRows" :key="row.title" class="select-none text-md">
          <td class="border-y border-md">
            <div class="select-none py-2 pr-3 text-lt">
              {{ row.title }}
            </div>
          </td>
          <td
            v-for="(value, index) in row.values"
            :key="index"
            :class="shownSubscriptionConfigs[index].active && 'bg-lt'"
            class="border border-md">
            <div class="px-3 py-2 text-center">
              {{ value }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <div v-else>
    <span class="text-md">To manage plans and billing for your workspace, use Dart in a browser or on a computer.</span>
  </div>
</template>
